<script setup lang="ts">
import { Mailing } from '@/@types/mailing'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogOverlay, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { TableBody, TableCell } from '@/components/ui/table-custom'
import Table from '@/components/ui/table-custom/Table.vue'
import TableHead from '@/components/ui/table-custom/TableHead.vue'
import TableHeader from '@/components/ui/table-custom/TableHeader.vue'
import TableRow from '@/components/ui/table-custom/TableRow.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDateFullWithHours } from '@/helpers/format-date'
import { Link, useForm } from '@inertiajs/vue3'
import { Eye, FileClock } from 'lucide-vue-next'
import { ref } from 'vue'

defineProps<{
    mailings: Mailing[]
}>()

const openDialogId = ref<number | null>(null)

const form = useForm({})

const successStatus = (status: string, isActive: boolean) => {
  const code = Number(status)

  // If mailing is not active and the response is 404, treat as success
  if (!isActive && code === 404) {
    return true
  }

  // Otherwise, consider standard 2xx as success
  return code >= 200 && code < 300
}
</script>

<template>
    <div class="mt-8 overflow-hidden">
        <Table class="lg:table-fixed">
            <TableHeader>
                <TableRow>
                    <TableHead>Número do pedido</TableHead>
                    <TableHead>Código do ingresso</TableHead>
                    <TableHead>Grupo do ingresso</TableHead>
                    <TableHead>Status do ingresso</TableHead>
                    <TableHead>E-mail participante</TableHead>
                    <TableHead>Status do pedido</TableHead>
                    <TableHead class="lg:w-[80px]">Ações</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <TableRow class="cursor-auto" v-for="mailing in mailings" :key="mailing.id">
                    <TableCell>
                        <strong>#{{ mailing.order_number }}</strong>
                    </TableCell>
                    <TableCell>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger class="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                                    {{ mailing.code }}
                                </TooltipTrigger>
                                <TooltipContent class="text-base shadow-lg">
                                    {{ mailing.code }}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </TableCell>
                    <TableCell>
                        {{ $t(`groups.${mailing.group}`) }}
                    </TableCell>
                    <TableCell> {{ $t(`mailing_status.${mailing.status}`) }} </TableCell>
                    <TableCell>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger class="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                                    {{ mailing.email }}
                                </TooltipTrigger>
                                <TooltipContent class="text-base shadow-lg">
                                    {{ mailing.email }}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </TableCell>
                    <TableCell>
                        <Badge :variant="mailing.order_status">{{ $t(mailing.order_status) }}</Badge>
                    </TableCell>
                    <TableCell>
                        <div class="flex items-center justify-center gap-2">
                            <Link :href="`/admin/orders/${mailing.order_number}`" class="aria-disabled:pointer-events-none aria-disabled:opacity-30">
                                <Eye class="size-5 min-w-5 text-gray-700" />
                                <span class="sr-only">{{ $t('Edit coupon') }}</span>
                            </Link>

                            <Dialog :open="openDialogId === mailing.id" @update:open="(val) => (openDialogId = val ? mailing.id : null)">
                                <DialogOverlay class="z-60" />
                                <DialogTrigger class="hover:text-destructive h-fit cursor-pointer text-gray-400">
                                    <span class="aria-disabled:pointer-events-none aria-disabled:opacity-30">
                                        <FileClock :class="`size-5 min-w-5 text-${!successStatus(mailing.yazo_status, mailing.is_active) ? 'red' : 'gray'}-700`" />
                                        <span class="sr-only">{{ $t('View') }}</span>
                                    </span>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>{{ $t('Last send to yazo') }}</DialogTitle>
                                    </DialogHeader>
                                    <div class="flex flex-col gap-4">
                                        <div v-if="mailing.yazo_status">
                                            <strong>{{ $t('Status') }}:</strong>
                                            {{
                                                successStatus(mailing.yazo_status, mailing.is_active)
                                                    ? `${$t('success')} - ${mailing.yazo_status}`
                                                    : `${$t('failed')} - ${mailing.yazo_status}`
                                            }}
                                        </div>
                                        <div>
                                            <strong>{{ $t('Sent at') }}:</strong>
                                            {{ mailing.yazo_status ? formatDateFullWithHours(mailing.yazo_sent_at) : '' }}
                                        </div>
                                        <div>
                                            <strong>{{ $t('Response') }}:</strong> {{ mailing.yazo_body }}
                                        </div>
                                    </div>
                                    <DialogFooter v-if="!successStatus(mailing.yazo_status, mailing.is_active)">
                                        <form @submit.prevent="form.post(`/admin/mailings/${mailing.id}/sync_in_yazo`)">
                                            <Button type="submit">{{ $t('Sync with yazo') }}</Button>
                                        </form>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </div>
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </div>
</template>
